import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogIn } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we're on a subpage (not homepage)
  const isSubpage = location.pathname !== '/';

  // Check if user is logged in
  const checkUserAuth = async () => {
    const token = localStorage.getItem('booking_token');
    if (!token) {
      setCurrentUser(null);
      return;
    }
    
    try {
      const res = await axios.get(`${API_URL}/api/booking/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(res.data);
    } catch (err) {
      localStorage.removeItem('booking_token');
      setCurrentUser(null);
    }
  };
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    // Listen for storage changes (login/logout from other tabs or same page)
    const handleStorageChange = (e) => {
      if (e.key === 'booking_token' || e.key === null) {
        checkUserAuth();
      }
    };

    // Custom event listener for same-tab login/logout
    const handleAuthChange = () => {
      checkUserAuth();
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authChange', handleAuthChange);
    
    handleResize();
    checkUserAuth();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, []);

  // Re-check auth when location changes (after login/logout)
  useEffect(() => {
    checkUserAuth();
  }, [location.pathname]);
  
  // Also check periodically in case token was set without navigation
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('booking_token');
      const hasUser = currentUser !== null;
      // If token state doesn't match user state, re-check
      if ((token && !hasUser) || (!token && hasUser)) {
        checkUserAuth();
      }
    }, 1000); // Check every second
    
    return () => clearInterval(interval);
  }, [currentUser]);

  const menuItems = [
    { label: t('infos'), href: '#infos' },
    { label: t('meeting_rooms'), href: '#meeting-rooms' },
    { label: t('offices'), href: '#offices' },
    { label: t('pedros'), href: '#pedros' },
    { label: t('events'), href: '#events' },
    { label: t('community'), href: '#community' },
    { label: t('contact'), href: '#contact' }
  ];

  // Handle menu click - navigate to homepage + anchor on subpages
  const handleMenuClick = (e, href) => {
    if (isSubpage) {
      e.preventDefault();
      navigate('/' + href);
    }
    setIsOpen(false);
  };

  const desktopTopStart = 'calc(50vh - 450px)';
  const mobileTopStart = 'calc(50vh - 120px)';

  return (
    <>
        {/* Mobile Toggle */}
        <div className="fixed top-4 right-4 z-50 flex gap-2 lg:hidden">
            <button 
                onClick={toggleLanguage} 
                className="p-2 bg-white/90 backdrop-blur rounded-full shadow-lg text-black transition-transform hover:scale-110 font-bold font-mono text-xs border border-concrete-200"
            >
                {language.toUpperCase()}
            </button>
            <button 
                onClick={() => setIsOpen(!isOpen)} 
                className="p-2 bg-white/90 backdrop-blur rounded-full shadow-lg text-black transition-transform hover:scale-110 border border-concrete-200"
                aria-label={isOpen ? "Menü schließen" : "Menü öffnen"}
                aria-expanded={isOpen}
            >
                {isOpen ? <X /> : <Menu />}
            </button>
        </div>

        {/* NAVBAR BACKGROUND - Always visible and white on subpages */}
        <nav 
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-1000 ease-in-out border-b ${
                isSubpage || scrolled 
                ? 'h-20 md:h-24 bg-white/95 backdrop-blur-md shadow-sm border-concrete-200' 
                : 'h-0 bg-transparent border-transparent delay-500' 
            }`}
        />

        {/* FLOATING LOGO */}
        <Link 
            to="/" 
            onClick={(e) => {
                // If already on homepage, scroll to top
                if (window.location.pathname === '/') {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }}
            className={`fixed z-50 transition-all duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            style={{
                top: (isSubpage || scrolled) ? (isMobile ? '16px' : '16px') : (isMobile ? mobileTopStart : desktopTopStart),
                left: (isSubpage || scrolled) ? (isMobile ? '16px' : '20px') : '50%',
                transform: (isSubpage || scrolled) ? 'translate(0, 0)' : 'translate(-50%, 0)',
                height: (isSubpage || scrolled) ? (isMobile ? '32px' : '50px') : 'auto', 
                width: (isSubpage || scrolled) ? 'auto' : (isMobile ? '200px' : '300px'),
                maxWidth: (isSubpage || scrolled) && isMobile ? 'calc(100vw - 120px)' : 'none',
                willChange: 'top, left, transform, width, height'
            }}
        >
            <div className="relative w-full h-full flex items-center justify-center">
                <img 
                    src="/assets/logo_full.svg" 
                    alt="Teamwerk" 
                    className={`transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] h-full w-auto object-contain ${
                        (isSubpage || scrolled) 
                        ? 'brightness-0' // Black when scrolled or on subpage
                        : 'brightness-0 invert drop-shadow-xl' // White in Hero
                    }`} 
                    style={{
                        filter: (isSubpage || scrolled) ? 'brightness(0)' : 'brightness(0) invert(1) drop-shadow(0 10px 15px rgba(0,0,0,0.3))'
                    }}
                />
            </div>
        </Link>

        {/* FLOATING EMBLEM (House) - Hidden on subpages and on mobile when scrolled */}
        {!isSubpage && (
        <div 
            className={`fixed z-40 pointer-events-none transition-all duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)] ${isOpen ? 'opacity-0' : ''}`}
            style={{
                top: scrolled ? '16px' : '50%',
                left: scrolled ? '210px' : '50%',
                transform: scrolled ? 'translate(0, 0)' : 'translate(-50%, -50%)',
                height: scrolled ? '50px' : 'auto', 
                width: scrolled ? 'auto' : (isMobile ? '280px' : '420px'), 
                opacity: scrolled ? (isMobile ? '0' : '1') : '0.45', 
                willChange: 'top, left, transform, width, height, opacity'
            }}
        >
             <img 
                src="/assets/emblem.svg" 
                alt="Emblem" 
                className={`w-full h-full object-contain transition-all duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                    scrolled 
                    ? 'filter brightness-0' // Black when scrolled
                    : 'filter brightness-0 invert drop-shadow-2xl' // White in Hero
                }`}
            />
        </div>
        )}

        {/* FLOATING MENU ITEMS (Desktop Only) */}
        <div 
            className="fixed z-50 hidden lg:flex items-center gap-6 xl:gap-8 transition-all duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{
                top: (isSubpage || scrolled) ? '36px' : 'calc(50vh - 330px)',
                left: (isSubpage || scrolled) ? 'auto' : '50%',
                right: (isSubpage || scrolled) ? '2rem' : 'auto',
                transform: (isSubpage || scrolled) ? 'translate(0, 0)' : 'translate(-50%, 0)',
                width: (isSubpage || scrolled) ? 'auto' : 'max-content',
                willChange: 'top, left, right, transform'
            }}
        >
            {menuItems.map((item) => (
                <a 
                    key={item.label} 
                    href={isSubpage ? '/' + item.href : item.href}
                    onClick={(e) => handleMenuClick(e, item.href)}
                    className={`text-[10px] xl:text-xs font-bold uppercase tracking-widest transition-all duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer ${
                        (isSubpage || scrolled) 
                        ? 'text-black hover:text-concrete-600' // Black when scrolled or on subpage
                        : 'text-white/90 hover:text-white hover:scale-105 drop-shadow-md'
                    }`}
                >
                    {item.label}
                </a>
            ))}
             
             {/* Language Toggle Desktop */}
             <button 
                onClick={toggleLanguage} 
                className={`transition-all duration-500 font-bold font-mono text-xs hover:scale-110 ${
                    (isSubpage || scrolled) ? 'text-black' : 'text-white'
                }`}
             >
                {language === 'de' ? 'EN' : 'DE'}
             </button>

             {/* User Button Desktop */}
             {currentUser ? (
                <button 
                    onClick={() => navigate('/mein-bereich')}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-500 hover:scale-105 ${
                        (isSubpage || scrolled) 
                            ? 'bg-black text-white hover:bg-black/80' 
                            : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                    }`}
                >
                    <User className="w-4 h-4" />
                    <span className="text-xs font-medium">
                        {currentUser.first_name?.charAt(0)}. {currentUser.last_name?.charAt(0)}.
                    </span>
                </button>
             ) : (
                <button 
                    onClick={() => navigate('/login')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-500 hover:scale-105 text-xs font-medium ${
                        (isSubpage || scrolled) 
                            ? 'bg-black text-white hover:bg-black/80' 
                            : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                    }`}
                >
                    <LogIn className="w-3.5 h-3.5" />
                    Anmelden
                </button>
             )}
        </div>

        {/* Mobile Menu Overlay - SOLID WHITE */}
        <div className={`fixed inset-0 z-40 bg-white backdrop-blur-none overflow-y-auto lg:hidden transition-all duration-500 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
            <div className="min-h-full flex flex-col items-center justify-center gap-6 py-12">
                <img src="/assets/logo_full.svg" alt="Teamwerk" className="h-20 w-auto mb-8 filter brightness-0" /> {/* Black Logo */}
                {menuItems.map((item) => (
                    <a 
                        key={item.label} 
                        href={isSubpage ? '/' + item.href : item.href}
                        onClick={(e) => handleMenuClick(e, item.href)}
                        className="text-lg font-bold text-black uppercase tracking-[0.15em] hover:text-concrete-500 transition-colors transform hover:scale-105"
                    >
                        {item.label}
                    </a>
                ))}
                
                {/* User Button Mobile */}
                {currentUser ? (
                    <button 
                        onClick={() => { navigate('/mein-bereich'); setIsOpen(false); }}
                        className="mt-4 flex items-center gap-2 px-6 py-3 bg-black text-white rounded-full font-medium hover:bg-black/80 transition-colors"
                    >
                        <User className="w-5 h-5" />
                        Mein Bereich
                    </button>
                ) : (
                    <button 
                        onClick={() => { navigate('/login'); setIsOpen(false); }}
                        className="mt-4 flex items-center gap-2 px-6 py-3 bg-black text-white rounded-full font-medium hover:bg-black/80 transition-colors"
                    >
                        <LogIn className="w-5 h-5" />
                        Anmelden
                    </button>
                )}
                
                <button 
                    onClick={() => { toggleLanguage(); setIsOpen(false); }}
                    className="px-4 py-2 border border-black/30 text-black rounded-full font-mono text-sm hover:bg-black/5"
                >
                    Switch to {language === 'de' ? 'English' : 'Deutsch'}
                </button>
            </div>
        </div>
    </>
  );
};
